import { CATALOGUE_ACTIONS } from 'modules/authorization/core';

import type { Action, CatalogueAction } from 'modules/authorization/core';

const CATALOGUE = new Set<string>(CATALOGUE_ACTIONS);

/**
 * Grant lists by canonical membership, so an unchanged answer keeps its identity.
 *
 * Bounded by construction: a key is a subset of the catalogue, so this can hold at most
 * 2^CATALOGUE_ACTIONS.length entries and in practice holds one per distinct role. Nothing evicts,
 * because nothing needs to.
 */
const bySignature = new Map<string, readonly CatalogueAction[]>();

/** One set per grant list, keyed on the list's identity - so the memo above carries through. */
const setsByList = new WeakMap<readonly Action[], ReadonlySet<Action>>();

/**
 * `allowed: string[]` from the wire to the grant list this app caches.
 *
 * **An array, not a `Set`, and deliberately so.** Every reader wants set semantics - `evaluate`
 * does membership tests - but `sentryReduxEnhancer` attaches the whole redux state to every Sentry
 * event, and a `Set` serialises to `{}`. Caching a set would make the permission state invisible
 * in precisely the reports someone opens to answer "why was this user denied?". It would also need
 * a `serializableCheck` exemption covering both the state path and the fulfilled action. Readers
 * call `asActionSet` instead, which costs one lookup and keeps the cache legible.
 *
 * Memoised on the *content*, not the response: RTK Query replaces its cache entry on every
 * refetch, so a fresh array each time would change `data`'s identity on a poll that changed
 * nothing, and every consumer memo keyed on it would recompute. Sorted into the key so the service
 * is free to reorder the array without that counting as a change - and sorted in the value too, so
 * two orderings of one answer are one cache entry rather than two equal-but-distinct ones.
 *
 * Unknown strings are dropped rather than rejected. The service may add an action before this app
 * knows the name - non-breaking per its ADR-0007 - and a grant we cannot name is a grant we cannot
 * ask about, so ignoring it is exactly right. This is also why a *rename* is invisible here and
 * has to be caught at build time instead (R20): the new name is discarded as unknown while the old
 * one simply stops arriving, which is indistinguishable from an ordinary denial.
 */
export const toAllowedActions = (allowed: readonly string[]): readonly CatalogueAction[] => {
  const known = allowed.filter((action): action is CatalogueAction => CATALOGUE.has(action)).sort();
  const signature = known.join('|');

  const memoised = bySignature.get(signature);
  if (memoised) return memoised;

  const frozen = Object.freeze(known);
  bySignature.set(signature, frozen);

  return frozen;
};

/**
 * The grant list as a set, for the membership tests every gate performs.
 *
 * Keyed on the list's identity rather than its content, because `toAllowedActions` has already
 * done the content work: one array per distinct answer means one set per distinct answer, with no
 * second signature to compute. A `WeakMap` so a list that falls out of the cache takes its set
 * with it.
 */
export const asActionSet = (actions: readonly Action[]): ReadonlySet<Action> => {
  const memoised = setsByList.get(actions);
  if (memoised) return memoised;

  const set: ReadonlySet<Action> = new Set<Action>(actions);
  setsByList.set(actions, set);

  return set;
};
